import React, { useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"
import {
  AD_SPEND_SUBTYPE_LABELS,
  AD_SPEND_SUBTYPES,
  type BucketGranularity,
  CUSTOM_METRIC_TYPE_LABELS,
  CUSTOM_METRIC_TYPES,
  type CustomMetricType,
  daysSinceWeekStart,
  deriveBucketEnd,
  isAdditiveFormat,
  resolveWeekStartDay,
  REVENUE_CHANNEL_SUBTYPE_LABELS,
  REVENUE_CHANNEL_SUBTYPES,
  subtypesForType,
  typedCustomMetricLabel,
  type WeekStartDay,
} from "~/shared/kpi"
import { useCompanyControllerGetFinancialsQuery } from "~/ui-rtk/api/companyApi"
import useCurrentCompany from "~/ui-rtk/hooks/current-company"
import {
  useCompanyCustomMetricControllerCreateMetricMutation,
  useCompanyCustomMetricControllerDeleteMetricMutation,
  useCompanyCustomMetricControllerDeleteValueMutation,
  useCompanyCustomMetricControllerListMetricsQuery,
  useCompanyCustomMetricControllerListValuesQuery,
  useCompanyCustomMetricControllerPreviewCsvMutation,
  useCompanyCustomMetricControllerUpdateMetricMutation,
  useCompanyCustomMetricControllerUpsertValuesMutation,
  type CustomMetricCsvPreviewDto,
  type CustomMetricFormat,
} from "~/ui-rtk/api/companyCustomMetricApi"
import { Button } from "~/ui-rtk/components/ui/controls"
import Input from "~/ui-rtk/components/ui/controls/Input"
import Select, {
  type TSelectOption,
} from "~/ui-rtk/components/ui/controls/Select"
import FileUpload from "~/ui-rtk/components/ui/controls/FileUpload"

interface CustomMetricManagerModalProps {
  isOpen: boolean
  onClose: () => void
  /** Pre-select this metric on open (the KPI table's per-row Upload CSV button). */
  initialMetricId?: string | null
}

const FORMAT_OPTIONS: TSelectOption[] = [
  { label: "Currency ($)", value: "currency" },
  { label: "Number", value: "number" },
  { label: "Percent (%)", value: "percent" },
  { label: "Decimal", value: "decimal" },
]

const GRANULARITY_OPTIONS: TSelectOption[] = [
  { label: "Daily", value: "day" },
  { label: "Weekly", value: "week" },
  { label: "Monthly", value: "month" },
]

// "" encodes null (untyped / no subtype) — Select values must be strings.
const TYPE_OPTIONS: TSelectOption[] = [
  { label: "None", value: "" },
  ...CUSTOM_METRIC_TYPES.map(type => ({
    label: CUSTOM_METRIC_TYPE_LABELS[type],
    value: type,
  })),
]

const SUBTYPE_OPTIONS_BY_TYPE: Partial<
  Record<CustomMetricType, TSelectOption[]>
> = {
  revenue: [
    { label: "No channel", value: "" },
    ...REVENUE_CHANNEL_SUBTYPES.map(subtype => ({
      label: REVENUE_CHANNEL_SUBTYPE_LABELS[subtype],
      value: subtype,
    })),
  ],
  ad_spend: [
    { label: "No category", value: "" },
    ...AD_SPEND_SUBTYPES.map(subtype => ({
      label: AD_SPEND_SUBTYPE_LABELS[subtype],
      value: subtype,
    })),
  ],
}

const TYPE_HELP: Record<CustomMetricType, string> = {
  revenue:
    "Adds into Total Business Revenue (and the channel's revenue table when a channel is set), plus dependent MERs.",
  ad_spend:
    "Adds into the Ad Spend table's Total (and Demand Gen/Capture spend when a category is set), plus dependent MERs and aMER.",
  variable_cost: "Subtracts from Total Business Contribution Margin.",
  fixed_cost:
    "Stored for future profitability metrics — not folded into any KPI yet.",
}

// Guidance a user can hand to an agent so it emits a directly-importable CSV.
const AGENT_CSV_GUIDANCE = [
  "Produce a CSV for a custom metric with exactly these columns and no others.",
  "",
  "For DAILY values use two columns — date,value — one row per day:",
  "  date,value",
  "  2026-01-01,1000",
  "  2026-01-02,1250.50",
  "",
  "For WEEKLY or MONTHLY values use three columns — start_date,end_date,value —",
  "where start_date/end_date are the bucket's first and last day:",
  "  start_date,end_date,value",
  "  2026-01-05,2026-01-11,7000",
  "",
  "Rules: dates are YYYY-MM-DD; value is a plain number (a leading $ and commas",
  "are tolerated); one granularity per file; no header renaming; no extra columns.",
].join("\n")

const DAILY_TEMPLATE = "date,value\n2026-01-01,1000\n2026-01-02,1250.50\n"

function monthStart(dateOnly: string): string {
  return `${dateOnly.slice(0, 7)}-01`
}

/** Snaps a date back to the company's reporting week-start so weekly values
 * align to the dashboard's week columns (mirrors connect.ts buildColumnBuckets). */
function snapToWeekStart(dateOnly: string, weekStartDay: WeekStartDay): string {
  const date = new Date(`${dateOnly}T00:00:00.000Z`)
  date.setUTCDate(
    date.getUTCDate() - daysSinceWeekStart(date.getUTCDay(), weekStartDay),
  )
  return date.toISOString().slice(0, 10)
}

/** Resolves the stored bucket [start, end] for a manually-entered value. */
function resolveBucket(
  dateOnly: string,
  granularity: BucketGranularity,
  weekStartDay: WeekStartDay,
): { start: string; end: string } {
  let start = dateOnly
  if (granularity === "month") {
    start = monthStart(dateOnly)
  } else if (granularity === "week") {
    start = snapToWeekStart(dateOnly, weekStartDay)
  }
  return { start, end: deriveBucketEnd(start, granularity) }
}

function formatBucketLabel(startDate: string, endDate: string): string {
  return startDate === endDate ? startDate : `${startDate} – ${endDate}`
}

const DATE_INPUT_LABEL: Record<BucketGranularity, string> = {
  day: "Date",
  week: "Week start date",
  month: "Any day in month",
}

const CustomMetricManagerModal: React.FC<CustomMetricManagerModalProps> = ({
  isOpen,
  onClose,
  initialMetricId,
}) => {
  const { data: metrics } = useCompanyCustomMetricControllerListMetricsQuery(
    undefined,
    { skip: !isOpen },
  )
  const currentCompany = useCurrentCompany()
  const { data: financials } = useCompanyControllerGetFinancialsQuery(
    { id: currentCompany?.id ?? "" },
    { skip: !currentCompany?.id },
  )
  const weekStartDay = useMemo(
    () => resolveWeekStartDay(financials),
    [financials],
  )
  const [createMetric, { isLoading: creating }] =
    useCompanyCustomMetricControllerCreateMetricMutation()
  const [updateMetric] = useCompanyCustomMetricControllerUpdateMetricMutation()
  const [deleteMetric] = useCompanyCustomMetricControllerDeleteMetricMutation()
  const [upsertValues] = useCompanyCustomMetricControllerUpsertValuesMutation()
  const [deleteValue] = useCompanyCustomMetricControllerDeleteValueMutation()
  const [previewCsv, { isLoading: previewing }] =
    useCompanyCustomMetricControllerPreviewCsvMutation()

  const [selectedId, setSelectedId] = useState<string | null>(null)
  const selected = useMemo(
    () => (metrics ?? []).find(metric => metric.id === selectedId) ?? null,
    [metrics, selectedId],
  )

  // New-metric form.
  const [newName, setNewName] = useState("")
  const [newFormat, setNewFormat] = useState<CustomMetricFormat>("currency")

  // Rename working copy (seeded from the selected metric).
  const [nameDraft, setNameDraft] = useState("")

  // Manual value entry.
  const [valGranularity, setValGranularity] =
    useState<BucketGranularity>("week")
  const [valDate, setValDate] = useState("")
  const [valAmount, setValAmount] = useState("")

  // CSV import.
  const [csvPreview, setCsvPreview] =
    useState<CustomMetricCsvPreviewDto | null>(null)

  const { data: values } = useCompanyCustomMetricControllerListValuesQuery(
    { id: selectedId ?? "" },
    { skip: !selectedId },
  )

  useEffect(() => {
    setNameDraft(selected?.name ?? "")
    setCsvPreview(null)
    setValDate("")
    setValAmount("")
  }, [selected])

  // Opened from a table row's Upload-CSV button: jump straight to that metric.
  useEffect(() => {
    if (isOpen && initialMetricId) {
      setSelectedId(initialMetricId)
    }
  }, [isOpen, initialMetricId])

  if (!isOpen) return null

  const handleCreate = async () => {
    const name = newName.trim()
    if (!name) return
    try {
      const created = await createMetric({
        body: { name, format: newFormat },
      }).unwrap()
      setNewName("")
      setNewFormat("currency")
      setSelectedId(created.id)
    } catch {
      toast.error("Could not create metric — the name may already be in use")
    }
  }

  const handleRename = async () => {
    const name = nameDraft.trim()
    if (!selected || !name || name === selected.name) return
    try {
      await updateMetric({ id: selected.id, body: { name } }).unwrap()
      toast.success("Renamed")
    } catch {
      toast.error("Could not rename — the name may already be in use")
    }
  }

  const handleFormatChange = async (format: CustomMetricFormat) => {
    if (!selected) return
    // A non-additive metric can't sum into currency aggregates, so switching
    // to percent/decimal clears the type (the server enforces this too).
    const body =
      !isAdditiveFormat(format) && selected.type
        ? { format, type: null, subtype: null }
        : { format }
    try {
      await updateMetric({ id: selected.id, body }).unwrap()
    } catch {
      toast.error("Could not update the metric")
    }
  }

  const handleTypeChange = async (value: string) => {
    if (!selected) return
    const type = value === "" ? null : (value as CustomMetricType)
    try {
      // A type change invalidates the old subtype; the picker starts blank.
      await updateMetric({
        id: selected.id,
        body: { type, subtype: null },
      }).unwrap()
    } catch {
      toast.error("Could not update the metric")
    }
  }

  const handleSubtypeChange = async (value: string) => {
    if (!selected?.type) return
    try {
      await updateMetric({
        id: selected.id,
        body: { type: selected.type, subtype: value === "" ? null : value },
      }).unwrap()
    } catch {
      toast.error("Could not update the metric")
    }
  }

  const handleDeleteMetric = async (id: string) => {
    if (
      !window.confirm(
        "Delete this custom metric and all of its values? This cannot be undone.",
      )
    ) {
      return
    }
    try {
      await deleteMetric({ id }).unwrap()
      if (selectedId === id) setSelectedId(null)
    } catch {
      toast.error("Could not delete the metric")
    }
  }

  const handleAddValue = async () => {
    if (!selected || !valDate) return
    const amount = Number(valAmount.replace(/[$,\s]/g, ""))
    if (!Number.isFinite(amount)) {
      toast.error("Enter a valid number")
      return
    }
    const { start, end } = resolveBucket(valDate, valGranularity, weekStartDay)
    try {
      await upsertValues({
        id: selected.id,
        body: { values: [{ startDate: start, endDate: end, amount }] },
      }).unwrap()
      setValAmount("")
      toast.success("Value saved")
    } catch {
      toast.error("Could not save value")
    }
  }

  const handleDeleteValue = async (valueId: string) => {
    if (!selected) return
    try {
      await deleteValue({ id: selected.id, valueId }).unwrap()
    } catch {
      toast.error("Could not delete the value")
    }
  }

  const handleCsvSelect = async (files: FileList | null) => {
    const file = files?.[0]
    if (!file || !selected) return
    setCsvPreview(null)
    try {
      const preview = await previewCsv({ id: selected.id, file }).unwrap()
      setCsvPreview(preview)
    } catch {
      toast.error("Could not read that CSV")
    }
  }

  const handleCsvApply = async () => {
    if (!selected || !csvPreview?.valid || csvPreview.rows.length === 0) return
    try {
      const result = await upsertValues({
        id: selected.id,
        body: { values: csvPreview.rows },
      }).unwrap()
      toast.success(
        `Imported ${result.created} new and updated ${result.updated} value${
          result.updated === 1 ? "" : "s"
        }`,
      )
      setCsvPreview(null)
    } catch {
      toast.error("Could not import values")
    }
  }

  const handleDownloadTemplate = () => {
    const blob = new Blob([DAILY_TEMPLATE], { type: "text/csv" })
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement("a")
    anchor.href = url
    anchor.download = "custom_metric_template.csv"
    anchor.click()
    URL.revokeObjectURL(url)
  }

  const handleCopyGuidance = async () => {
    try {
      await navigator.clipboard.writeText(AGENT_CSV_GUIDANCE)
      toast.success("Format instructions copied")
    } catch {
      toast.error("Could not copy to clipboard")
    }
  }

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-system-blue-900 border border-system-blue-700 rounded-system-lg shadow-2xl w-full max-w-4xl mx-4 max-h-[90vh] flex flex-col">
        <div className="p-6 flex items-center justify-between border-b border-system-blue-700">
          <h2 className="text-xl font-semibold text-white">Custom metrics</h2>
          <button
            onClick={onClose}
            className="text-system-blue-300 hover:text-white transition-colors"
            aria-label="Close"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="flex flex-1 min-h-0">
          {/* Metric list + create */}
          <div className="w-64 border-r border-system-blue-700 flex flex-col">
            <div className="flex-1 overflow-y-auto p-3 space-y-1">
              {(metrics ?? []).length === 0 && (
                <p className="text-sm text-system-blue-300 p-2">
                  No custom metrics yet.
                </p>
              )}
              {(metrics ?? []).map(metric => (
                <button
                  key={metric.id}
                  onClick={() => setSelectedId(metric.id)}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                    metric.id === selectedId
                      ? "bg-system-blue-700 text-white"
                      : "text-system-blue-100 hover:bg-system-blue-800"
                  }`}
                >
                  <span className="block truncate">{metric.name}</span>
                  <span className="block text-xs text-system-blue-300">
                    {metric.format}
                    {metric.type
                      ? ` · ${typedCustomMetricLabel(metric.type, metric.subtype)}`
                      : ""}
                  </span>
                </button>
              ))}
            </div>
            <div className="p-3 border-t border-system-blue-700 space-y-2">
              <Input
                fullWidth
                className="h-9"
                placeholder="New metric name"
                value={newName}
                onChange={event => setNewName(event.target.value)}
              />
              <Select
                className="select-sm min-h-0 h-9 text-sm"
                options={FORMAT_OPTIONS}
                value={newFormat}
                onChange={event =>
                  setNewFormat(event.target.value as CustomMetricFormat)
                }
              />
              <Button
                variant={{ variant: "solid", color: "blue" }}
                size="sm"
                className="w-full h-9"
                onClick={handleCreate}
                disabled={creating || newName.trim().length === 0}
              >
                Add metric
              </Button>
            </div>
          </div>

          {/* Detail */}
          <div className="flex-1 overflow-y-auto p-6">
            {!selected ? (
              <p className="text-system-blue-300 text-sm">
                Select a metric to edit its settings and values, or create one.
              </p>
            ) : (
              <div className="space-y-6">
                {/* Settings */}
                <div className="space-y-3">
                  <div className="flex items-end gap-2">
                    <div className="flex-1">
                      <label className="block mb-1 text-xs text-system-blue-300">
                        Name
                      </label>
                      <Input
                        fullWidth
                        className="h-9"
                        value={nameDraft}
                        onChange={event => setNameDraft(event.target.value)}
                        onBlur={handleRename}
                      />
                    </div>
                    <div className="w-40">
                      <label className="block mb-1 text-xs text-system-blue-300">
                        Format
                      </label>
                      <Select
                        className="select-sm min-h-0 h-9 text-sm"
                        options={FORMAT_OPTIONS}
                        value={selected.format}
                        onChange={event =>
                          handleFormatChange(
                            event.target.value as CustomMetricFormat,
                          )
                        }
                      />
                    </div>
                    <Button
                      variant={{ variant: "solid", color: "red" }}
                      size="sm"
                      className="h-9"
                      onClick={() => handleDeleteMetric(selected.id)}
                    >
                      Delete
                    </Button>
                  </div>
                  {isAdditiveFormat(selected.format) ? (
                    <div className="space-y-1">
                      <div className="flex items-end gap-2">
                        <div className="w-44">
                          <label className="block mb-1 text-xs text-system-blue-300">
                            Metric type
                          </label>
                          <Select
                            className="select-sm min-h-0 h-9 text-sm"
                            options={TYPE_OPTIONS}
                            value={selected.type ?? ""}
                            onChange={event =>
                              handleTypeChange(event.target.value)
                            }
                          />
                        </div>
                        {selected.type &&
                          subtypesForType(selected.type) !== null && (
                            <div className="w-56">
                              <label className="block mb-1 text-xs text-system-blue-300">
                                {selected.type === "revenue"
                                  ? "Channel"
                                  : "Demand category"}
                              </label>
                              <Select
                                className="select-sm min-h-0 h-9 text-sm"
                                options={
                                  SUBTYPE_OPTIONS_BY_TYPE[selected.type] ?? []
                                }
                                value={selected.subtype ?? ""}
                                onChange={event =>
                                  handleSubtypeChange(event.target.value)
                                }
                              />
                            </div>
                          )}
                      </div>
                      <p className="text-xs text-system-blue-300">
                        {selected.type
                          ? TYPE_HELP[selected.type]
                          : "Typing a metric automatically adds its values into the matching KPI dashboard totals."}
                      </p>
                    </div>
                  ) : (
                    <p className="text-xs text-system-blue-300">
                      Percent and decimal metrics can't be typed — only currency
                      or number metrics can sum into KPI totals.
                    </p>
                  )}
                </div>

                {/* Manual entry */}
                <div className="space-y-2 border-t border-system-blue-700 pt-4">
                  <p className="text-sm font-semibold text-white">
                    Enter a value
                  </p>
                  <div className="flex items-end gap-2">
                    <div className="w-32">
                      <label className="block mb-1 text-xs text-system-blue-300">
                        Granularity
                      </label>
                      <Select
                        className="select-sm min-h-0 h-9 text-sm"
                        options={GRANULARITY_OPTIONS}
                        value={valGranularity}
                        onChange={event =>
                          setValGranularity(
                            event.target.value as BucketGranularity,
                          )
                        }
                      />
                    </div>
                    <div className="w-40">
                      <label className="block mb-1 text-xs text-system-blue-300">
                        {DATE_INPUT_LABEL[valGranularity]}
                      </label>
                      <input
                        type="date"
                        value={valDate}
                        onChange={event => setValDate(event.target.value)}
                        className="h-9 w-full rounded-lg bg-basic-dark-blue border border-basic-blue text-white text-sm px-2 py-0 focus:ring-0 [color-scheme:dark]"
                      />
                    </div>
                    <div className="w-32">
                      <label className="block mb-1 text-xs text-system-blue-300">
                        Value
                      </label>
                      <Input
                        fullWidth
                        className="h-9"
                        value={valAmount}
                        onChange={event => setValAmount(event.target.value)}
                        placeholder="0.00"
                      />
                    </div>
                    <Button
                      variant={{ variant: "solid", color: "blue" }}
                      size="sm"
                      className="h-9"
                      onClick={handleAddValue}
                      disabled={!valDate || valAmount.trim() === ""}
                    >
                      Save
                    </Button>
                  </div>
                  {valDate && valGranularity !== "day" && (
                    <p className="text-xs text-system-blue-300">
                      Saves to{" "}
                      {(() => {
                        const { start, end } = resolveBucket(
                          valDate,
                          valGranularity,
                          weekStartDay,
                        )
                        return formatBucketLabel(start, end)
                      })()}
                    </p>
                  )}
                </div>

                {/* CSV import */}
                <div className="space-y-2 border-t border-system-blue-700 pt-4">
                  <p className="text-sm font-semibold text-white">
                    Import from CSV
                  </p>
                  <div className="flex items-center gap-3">
                    <FileUpload
                      accept=".csv,text/csv"
                      maxSize={5}
                      maxFiles={1}
                      text={previewing ? "Reading…" : "Choose CSV"}
                      onUpload={handleCsvSelect}
                      classes={{ button: "h-9 py-2 px-4 text-sm" }}
                    />
                    <button
                      className="text-xs text-system-brand-pink-400 hover:text-system-brand-pink-300 underline transition-colors"
                      onClick={handleDownloadTemplate}
                    >
                      Download template
                    </button>
                    <button
                      className="text-xs text-system-brand-pink-400 hover:text-system-brand-pink-300 underline transition-colors"
                      onClick={handleCopyGuidance}
                    >
                      Copy format instructions
                    </button>
                  </div>

                  {csvPreview && !csvPreview.valid && (
                    <div className="p-3 bg-system-error-500/10 border border-system-error-500/30 rounded-lg space-y-1 max-h-40 overflow-y-auto">
                      <p className="text-sm text-white font-medium">
                        This file has errors — nothing was imported.
                      </p>
                      {csvPreview.errors.map((error, index) => (
                        <p key={index} className="text-xs text-platinum">
                          {error.row !== undefined ? `Line ${error.row}: ` : ""}
                          {error.message}
                        </p>
                      ))}
                    </div>
                  )}

                  {csvPreview && csvPreview.valid && (
                    <div className="p-3 bg-basic-blue/10 border border-basic-blue/30 rounded-lg flex items-center justify-between">
                      <p className="text-sm text-platinum">
                        {csvPreview.stats.newCount} new,{" "}
                        {csvPreview.stats.overwriteCount} will overwrite
                        existing values.
                      </p>
                      <Button
                        variant={{ variant: "solid", color: "blue" }}
                        size="sm"
                        className="h-9 shrink-0"
                        onClick={handleCsvApply}
                      >
                        Apply {csvPreview.rows.length} values
                      </Button>
                    </div>
                  )}
                </div>

                {/* Existing values */}
                <div className="space-y-2 border-t border-system-blue-700 pt-4">
                  <p className="text-sm font-semibold text-white">
                    Values ({(values ?? []).length})
                  </p>
                  <div className="max-h-52 overflow-y-auto">
                    {(values ?? []).length === 0 ? (
                      <p className="text-xs text-system-blue-300">
                        No values entered yet.
                      </p>
                    ) : (
                      <table className="w-full text-sm">
                        <tbody>
                          {(values ?? []).map(value => (
                            <tr
                              key={value.id}
                              className="border-b border-system-blue-800"
                            >
                              <td className="py-1 text-system-blue-100">
                                {formatBucketLabel(
                                  value.startDate,
                                  value.endDate,
                                )}
                              </td>
                              <td className="py-1 text-right text-white">
                                {value.amount}
                              </td>
                              <td className="py-1 text-right w-10">
                                <button
                                  onClick={() => handleDeleteValue(value.id)}
                                  className="text-system-error-400 hover:text-system-error-300 text-xs"
                                  aria-label="Delete value"
                                >
                                  ✕
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="p-4 border-t border-system-blue-700 flex justify-end">
          <Button
            variant={{ variant: "solid", color: "blue" }}
            size="sm"
            className="h-9"
            onClick={onClose}
          >
            Done
          </Button>
        </div>
      </div>
    </div>
  )
}

export default CustomMetricManagerModal
